import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Spinner } from '../../components';
import { getCurrentUser } from '../../data/session';
import {
  selectPlan,
  refreshSession,
  logout,
  friendlyAuthError,
  routeAfterAuth,
} from '../../services/auth-service';

type Plan = 'solo' | 'team';

const PLAN_INFO: Array<{
  id: Plan;
  name: string;
  tagline: string;
  price: string;
  priceNote: string;
  features: string[];
  featured?: boolean;
}> = [
  {
    id: 'solo',
    name: 'Solo Founder',
    tagline: 'Build solo, learn in public.',
    price: '$10',
    priceNote: 'One-time',
    features: [
      'Full 6-phase journey',
      'Complete LMS access',
      'Group mentor sessions',
      'Phase & completion certificates',
      'Pitch to referred investors',
    ],
  },
  {
    id: 'team',
    name: 'Team',
    tagline: 'Your whole founding team, one account.',
    price: '$30',
    priceNote: 'One-time',
    featured: true,
    features: [
      'Everything in Solo Founder',
      'Up to 4 team seats',
      'Shared startup profile',
      'Team-based progress tracking',
      'Cofounder workspace',
    ],
  },
];

const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const Plans: React.FC = () => {
  const navigate = useNavigate();
  const me = getCurrentUser();

  const [busyPlan, setBusyPlan] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Processing animation (after the request resolves)
  const [processing, setProcessing] = useState(false);
  const [statusMsg, setStatusMsg] = useState('Setting up your dashboard…');
  const [showCheck, setShowCheck] = useState(false);

  // Bootstrap: only founders without a plan should be here. Anything else, route them properly.
  useEffect(() => {
    let alive = true;
    (async () => {
      // No session at all → /login
      if (!me.id) {
        navigate('/login');
        return;
      }
      // Hit the backend to get authoritative state (handles "logged in via demo" too — refreshSession returns null then).
      try {
        const r = await refreshSession();
        if (!alive) return;
        if (r) {
          const target = routeAfterAuth(r.user);
          if (target !== '/select-plan') navigate(target);
        } else if (me.role !== 'founder') {
          navigate(`/app/dashboard/${me.role}`);
        }
      } catch {
        // swallow — user can still pick a plan if they're already on the right page
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runProcessing = (after: () => void) => {
    setProcessing(true);
    setStatusMsg('Setting up your dashboard…');
    setShowCheck(false);
    setTimeout(() => setStatusMsg('Welcome to BunyadKar'), 800);
    setTimeout(() => setShowCheck(true), 1300);
    setTimeout(after, 1700);
  };

  const onPick = async (p: Plan) => {
    if (busyPlan) return;
    setBusyPlan(p);
    setError(null);
    try {
      const user = await selectPlan(p);
      runProcessing(() => navigate(`/app/dashboard/${user.role}`));
    } catch (err: any) {
      setError(friendlyAuthError(err) || 'Could not save your plan — please try again.');
      setBusyPlan(null);
    }
  };

  const onSignOut = async () => {
    await logout();
    navigate('/login');
  };

  if (processing) {
    return (
      <div className="fixed inset-0 bg-bg/95 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
        <div className="text-center space-y-6 px-6">
          <div className="relative w-20 h-20 mx-auto">
            {!showCheck && (
              <div className="w-20 h-20 border-4 border-chart/20 border-t-chart rounded-full animate-spin" />
            )}
            {showCheck && (
              <div className="w-20 h-20 bg-success/15 rounded-full flex items-center justify-center animate-fade-in-up">
                <CheckIcon className="w-10 h-10 text-success" />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <p className="font-display text-2xl tracking-wide text-text uppercase">{statusMsg}</p>
            <p className="font-serif-display text-base italic text-muted">
              Hang tight — this will only take a moment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="flex items-center justify-between px-6 py-4 border-b border-line bg-shell">
        <Link to="/" className="inline-flex items-center">
          <img src="/logo.png" alt="BunyadKar" className="h-8 w-auto opacity-80" />
        </Link>
        <button
          type="button"
          onClick={onSignOut}
          className="text-sm text-muted hover:text-text transition-colors"
        >
          Sign out
        </button>
      </div>

      <div className="flex-1 px-6 py-12 flex items-start justify-center">
        <div className="w-full max-w-4xl">
          <div className="text-center mb-10">
            <h1 className="font-display text-4xl md:text-5xl tracking-wide text-text uppercase mb-3">
              Pick your plan
            </h1>
            <p className="font-serif-display text-xl md:text-2xl italic text-muted max-w-2xl mx-auto">
              One last step — pick how you'll build. You can change this later.
            </p>
          </div>

          {error && (
            <div className="max-w-md mx-auto mb-6">
              <Card>
                <p className="text-danger text-sm text-center">{error}</p>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PLAN_INFO.map((p) => {
              const base = 'relative flex flex-col h-full rounded-card bg-shell p-7 transition-all duration-200 text-left';
              const visual = p.featured
                ? 'border-2 border-chart shadow-lg shadow-chart/10'
                : 'border border-line hover:border-chart/40 hover:shadow-md';
              const isBusy = busyPlan === p.id;
              const otherBusy = busyPlan !== null && busyPlan !== p.id;
              return (
                <div key={p.id} className={`${base} ${visual}`}>
                  {p.featured && (
                    <span className="absolute -top-3 right-6 bg-chart text-white text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full shadow-md">
                      Most Popular
                    </span>
                  )}
                  <div className="mb-5">
                    <h3 className="font-display text-3xl tracking-wide text-text uppercase mb-2">{p.name}</h3>
                    <p className="font-serif-display text-lg italic text-muted">{p.tagline}</p>
                  </div>
                  <div className="mb-6 pb-6 border-b border-line">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display text-5xl text-text">{p.price}</span>
                      <span className="text-sm text-muted">{p.priceNote}</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-8 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-text text-sm leading-relaxed">
                        <CheckIcon className="w-5 h-5 text-chart flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => onPick(p.id)}
                    disabled={busyPlan !== null}
                    className={`mt-auto w-full inline-flex items-center justify-center gap-2 py-3 rounded-card font-medium text-sm transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${
                      p.featured
                        ? 'bg-chart text-white hover:bg-chart/90 shadow-sm hover:shadow-chart/20'
                        : 'bg-shell border border-line text-text hover:border-chart/40 hover:bg-surface'
                    }`}
                  >
                    {isBusy && <Spinner className="w-4 h-4" />}
                    {isBusy ? 'Saving…' : otherBusy ? ' ' : `Continue with ${p.name} — ${p.price}`}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-muted mt-8 max-w-xl mx-auto">
            Payment processing is coming soon. For now you'll proceed straight to your dashboard.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Plans;
